<template>
  <div class="pdf-viewer">
    <nav-bar
        fixed
        z-index="99"
        title="合同查看"
        left-text="返回"
        left-arrow
        @click-left="$router.go(-1)"
    >
      <!-- 右侧操作按钮 -->
      <template #right>
        <div class="nav-right-buttons">
          <van-icon name="browser-o" size="20" @click="openInNewTab" title="在浏览器中打开" />
          <van-icon name="down" size="20" @click="downloadPdf" class="download-icon" title="下载PDF" />
        </div>
      </template>
    </nav-bar>
    <div style="height: 46px"></div>

    <div class="pdf-container">
      <!-- PDF.js渲染区域 -->
      <div v-if="!loading && !loadError" ref="pdfContainer" class="pdf-js-container">
        <div v-for="n in numPages" :key="n" :id="`pdf-page-${n}`" class="pdf-page"></div>
      </div>

      <!-- 加载中提示 -->
      <div v-if="loading" class="loading-container">
        <van-loading type="spinner" color="#0ce6df" />
        <p>加载中...</p>
      </div>

      <!-- 加载失败提示 -->
      <div v-if="loadError" class="error-container">
        <p>{{ errorMessage }}</p>
        <div class="error-buttons">
          <van-button type="primary" size="small" @click="openInNewTab">在浏览器中打开</van-button>
          <van-button plain size="small" @click="downloadPdf">下载PDF</van-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { NavBar, Loading, Button, Dialog, Icon } from 'vant';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/build/pdf.worker';

export default {
  name: 'PdfViewer',
  components: {
    NavBar,
    VanLoading: Loading,
    VanButton: Button,
    VanIcon: Icon
  },
  data() {
    return {
      pdfUrl: '',
      originalUrl: '',
      loading: true,
      loadError: false,
      errorMessage: '无法在当前浏览器中预览PDF，请尝试其他方式查看',
      pdf: null,
      numPages: 0,
      currentPage: 1,
      pdfLib: pdfjsLib
    };
  },
  created() {
    this.initPdfViewer();
  },
  mounted() {
    // 设置worker路径
    this.pdfLib.GlobalWorkerOptions.workerSrc = window.location.origin + '/pdf.worker.js';
  },
  methods: {
    initPdfViewer() {
      // 获取URL参数中的PDF地址
      const urlParam = this.$route.query.url;
      if (!urlParam) {
        this.loading = false;
        this.loadError = true;
        this.errorMessage = '未找到合同地址';
        return;
      }

      try {
        // 解码URL参数
        this.originalUrl = decodeURIComponent(urlParam);
        this.pdfUrl = this.originalUrl;
        this.renderPdf();
      } catch (error) {
        console.error('PDF预览初始化失败:', error);
        this.loading = false;
        this.loadError = true;
      }
    },
    renderPdf() {
      if (!this.pdfLib) {
        this.loadError = true;
        this.loading = false;
        return;
      }

      // 使用PDF.js加载并渲染PDF
      const loadingTask = this.pdfLib.getDocument(this.pdfUrl);
      loadingTask.promise.then(pdf => {
        this.pdf = pdf;
        this.numPages = pdf.numPages;
        this.loading = false;

        // 渲染所有页面
        for (let i = 1; i <= pdf.numPages; i++) {
          this.renderPage(i);
        }
      }).catch(error => {
        console.error('加载PDF失败:', error);
        this.loading = false;
        this.loadError = true;
      });
    },
    renderPage(pageNumber) {
      this.pdf.getPage(pageNumber).then(page => {
        const viewport = page.getViewport(1.0);
        const container = document.getElementById(`pdf-page-${pageNumber}`);

        if (!container) return;

        // 创建canvas元素
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // 设置canvas样式
        canvas.style.width = '100%';
        canvas.style.height = 'auto';

        // 清空容器并添加canvas
        container.innerHTML = '';
        container.appendChild(canvas);

        // 渲染PDF页面到canvas
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        page.render(renderContext);
      });
    },
    openInNewTab() {
      // 在新标签页中打开PDF
      if (this.originalUrl) {
        window.open(this.originalUrl, '_blank');
        this.$toast('已在浏览器中打开');
      } else {
        this.$dialog.alert({
          title: '提示',
          message: '链接无效'
        });
      }
    },
    downloadPdf() {
      if (this.originalUrl) {
        // 创建一个隐藏的a标签用于下载
        const a = document.createElement('a');
        a.href = this.originalUrl;
        a.download = '灵工合同.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        this.$toast('开始下载合同');
      } else {
        Dialog.alert({
          title: '提示',
          message: '下载链接无效'
        });
      }
    }
  }
};
</script>

<style scoped>
.pdf-viewer {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f5f5f5;
}

.nav-right-buttons {
  display: flex;
  align-items: center;
  padding-right: 10px;
}

.nav-right-buttons .van-icon {
  margin-left: 15px;
  color: #333;
}

.download-icon {
  position: relative;
  top: 1px;
}

.pdf-container {
  flex: 1;
  position: relative;
  overflow-y: auto;
  background-color: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.pdf-js-container {
  padding: 10px;
}

.pdf-page {
  margin: 10px auto;
  background-color: white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  max-width: 100%;
}

.loading-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.error-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: #666;
  width: 90%;
  max-width: 320px;
}

.error-container p {
  margin-bottom: 15px;
  font-size: 14px;
}

.error-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .pdf-page {
    width: 100%;
  }

  .error-container {
    padding: 0 15px;
  }
}
</style>
